import React, { useEffect, useMemo, useState } from "react";
import { api, PROGRAM_LABELS, formatErr } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Video, Star, Upload } from "lucide-react";
import { resolveImg } from "@/lib/utils";

const EMPTY = { author_name: "", role: "", text: "", video_url: "", thumbnail_url: "", image_url: "", program_type: "meditation_enlightenment", track: "both", display_order: 100, status: "published", country: "" };

export default function TestimonialsAdminPage() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [filters, setFilters] = useState({ status: "", program: "" });
  const [uploading, setUploading] = useState(false);

  const load = async () => { const { data } = await api.get("/admin/testimonials"); setItems(data); };
  useEffect(() => { load(); }, []);

  const upd = (k, v) => setForm(s => ({ ...s, [k]: v }));

  const uploadImage = async (file) => {
    if (!file) return;
    const ext = (file.name.split(".").pop() || "").toLowerCase();
    if (!["jpg", "jpeg", "png", "webp"].includes(ext)) { toast.error("Use JPEG, JPG, PNG or WEBP"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/testimonials/upload-image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      upd("image_url", data.image_url);
      toast.success("Image uploaded");
    } catch (e) { toast.error(formatErr(e)); }
    finally { setUploading(false); }
  };
  const save = async () => {
    if (!form.author_name) { toast.error("Author name is required"); return; }
    if (!form.text && !form.video_url && !form.image_url && !form.thumbnail_url) {
      toast.error("Add a video URL, an image, or testimonial text"); return;
    }
    try {
      const payload = { ...form, display_order: Number(form.display_order) || 100 };
      if (editingId) await api.patch(`/admin/testimonials/${editingId}`, payload);
      else await api.post("/admin/testimonials", payload);
      toast.success("Saved"); setOpen(false); load();
    } catch (e) { toast.error(formatErr(e)); }
  };
  const del = async (id) => { if (!window.confirm("Delete this testimonial?")) return; await api.delete(`/admin/testimonials/${id}`); load(); };
  const toggleStatus = async (t) => {
    const next = t.status === "published" ? "draft" : "published";
    try { await api.patch(`/admin/testimonials/${t.id}`, { status: next }); toast.success(next === "published" ? "Published" : "Moved to draft"); load(); }
    catch (e) { toast.error(formatErr(e)); }
  };

  const shown = useMemo(() => items.filter(t =>
    (!filters.status || t.status === filters.status) &&
    (!filters.program || t.program_type === filters.program)
  ), [items, filters]);

  return (
    <div className="space-y-6 fade-up" data-testid="admin-testimonials">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-serif text-4xl">Testimonials</h1>
          <p className="text-sm text-[#7E736E]">{items.filter(t=>t.status==="published").length} published · {items.length} total</p>
        </div>
        <button data-testid="new-testimonial" onClick={() => { setForm(EMPTY); setEditingId(null); setOpen(true); }} className="btn-primary">+ Add testimonial</button>
      </div>

      <div className="soft-card !p-5 grid sm:grid-cols-3 gap-3">
        <Select value={filters.status} onValueChange={v => setFilters({ ...filters, status: v === "all" ? "" : v })}>
          <SelectTrigger data-testid="t-filter-status"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All statuses</SelectItem><SelectItem value="published">Published</SelectItem><SelectItem value="draft">Draft</SelectItem></SelectContent>
        </Select>
        <Select value={filters.program} onValueChange={v => setFilters({ ...filters, program: v === "all" ? "" : v })}>
          <SelectTrigger data-testid="t-filter-program"><SelectValue placeholder="Program" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All programs</SelectItem>{Object.entries(PROGRAM_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
        </Select>
        <button onClick={() => setFilters({ status: "", program: "" })} className="btn-ghost text-sm">Clear</button>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {shown.map(t => (
          <div key={t.id} className="soft-card !p-6 flex flex-col" data-testid={`testimonial-${t.id}`}>
            <div className="flex items-start justify-between">
              <div>
                <div className="font-serif text-xl">{t.author_name}</div>
                <div className="text-xs text-[#7E736E]">{t.role}{t.country ? ` · ${t.country}` : ""}</div>
              </div>
              {t.video_url ? <Video className="w-4 h-4 text-terracotta" /> : <Star className="w-4 h-4 text-[#D9B26F]" />}
            </div>
            <p className="mt-3 text-sm flex-1">"{t.text}"</p>
            <div className="mt-3 text-xs flex items-center gap-2 flex-wrap">
              <span className={`badge-soft ${t.status === "published" ? "bg-[#D4E4D3] text-[#2F4F2F]" : "bg-[#F3EFEA] text-[#5C5955]"}`}>{t.status}</span>
              <span className="text-[#7E736E]">{PROGRAM_LABELS[t.program_type]}</span>
              <span className="text-[#9C928D]">#{t.display_order}</span>
            </div>
            <div className="mt-3 flex gap-2">
              <button onClick={() => { setForm({ ...EMPTY, ...t }); setEditingId(t.id); setOpen(true); }} className="btn-ghost text-xs" data-testid={`edit-${t.id}`}>Edit</button>
              <button onClick={() => toggleStatus(t)} className="text-xs underline" data-testid={`toggle-${t.id}`}>{t.status === "published" ? "Unpublish" : "Publish"}</button>
              <button onClick={() => del(t.id)} className="text-xs text-red-700 underline ml-auto" data-testid={`delete-${t.id}`}>Delete</button>
            </div>
          </div>
        ))}
        {shown.length === 0 && <div className="soft-card !p-8 text-center text-[#7E736E] col-span-full">No testimonials match these filters.</div>}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-xl max-h-[90vh] overflow-y-auto" data-testid="testimonial-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">{editingId ? "Edit" : "New"} testimonial</DialogTitle>
            <DialogDescription className="text-xs text-[#7E736E]">Published testimonials appear on the public site, ordered by display order (lower first).</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Author name*</Label><Input value={form.author_name} onChange={e => upd("author_name", e.target.value)} data-testid="t-name" /></div>
              <div><Label>Country</Label><Input value={form.country} onChange={e => upd("country", e.target.value)} data-testid="t-country" /></div>
            </div>
            <div><Label>Role / location</Label><Input value={form.role} onChange={e => upd("role", e.target.value)} /></div>
            <div><Label>Text{form.video_url || form.image_url || form.thumbnail_url ? "" : "*"}</Label><Textarea rows={3} value={form.text} onChange={e => upd("text", e.target.value)} data-testid="t-text" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Video URL (YouTube / Shorts)</Label><Input value={form.video_url} onChange={e => upd("video_url", e.target.value)} data-testid="t-video-url" placeholder="https://youtube.com/shorts/..." /></div>
              <div><Label>Thumbnail URL</Label><Input value={form.thumbnail_url} onChange={e => upd("thumbnail_url", e.target.value)} placeholder="https://..." /></div>
            </div>

            <div className="rounded-xl border border-dashed border-[#D9CFC2] p-4 bg-[#FBF8F3]">
              <Label className="text-sm">Static testimonial image (for Static section)</Label>
              <p className="text-xs text-[#9C928D] mt-0.5">Upload a WhatsApp screenshot, written feedback graphic, Zoom screenshot or event photo. JPEG / JPG / PNG / WEBP.</p>
              <div className="mt-3 flex items-center gap-3 flex-wrap">
                <label className="btn-ghost text-xs cursor-pointer inline-flex items-center gap-2" data-testid="t-image-upload-label">
                  <Upload className="w-4 h-4" /> {uploading ? "Uploading…" : "Upload image"}
                  <input type="file" accept="image/jpeg,image/jpg,image/png,image/webp" className="hidden"
                    data-testid="t-image-upload" disabled={uploading}
                    onChange={e => uploadImage(e.target.files?.[0])} />
                </label>
                {form.image_url && (
                  <button type="button" onClick={() => upd("image_url", "")} className="text-xs text-red-700 underline" data-testid="t-image-clear">Remove image</button>
                )}
              </div>
              {form.image_url && <img src={resolveImg(form.image_url)} alt="uploaded" className="mt-3 rounded-lg max-h-40 object-cover" data-testid="t-image-preview" />}
            </div>

            {form.thumbnail_url && !form.image_url && <img src={form.thumbnail_url} alt="preview" className="rounded-lg max-h-32 object-cover" />}
            <div className="grid grid-cols-3 gap-3">
              <div><Label>Program</Label>
                <Select value={form.program_type} onValueChange={v => upd("program_type", v)}>
                  <SelectTrigger data-testid="t-program"><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.entries(PROGRAM_LABELS).map(([k, v]) => (<SelectItem key={k} value={k}>{v}</SelectItem>))}</SelectContent>
                </Select>
              </div>
              <div><Label>Order</Label><Input type="number" value={form.display_order} onChange={e => upd("display_order", e.target.value)} data-testid="t-order" /></div>
              <div><Label>Status</Label>
                <Select value={form.status} onValueChange={v => upd("status", v)}>
                  <SelectTrigger data-testid="t-status"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="published">Published</SelectItem><SelectItem value="draft">Draft</SelectItem></SelectContent>
                </Select>
              </div>
            </div>
            <button onClick={save} className="btn-primary w-full" data-testid="t-save">Save</button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
